"use client";

import { useMemo } from "react";
import { useAuth } from "../components/AuthProvider";

// ─── Always-On Features ───────────────────────────────────────────────────────
// These are never gated by plan modules — always accessible.
const ALWAYS_ON_MODULES = new Set(["settings", "notifications", "reports"]);

// ─── Permission-to-Module Mapping ─────────────────────────────────────────────
// Maps backend role permissions to plan module names.
// Kept for sidebar module-name resolution.
const PERMISSION_TO_MODULE: Record<string, string> = {
  "vehicle management": "vehicles",
  "booking access": "bookings",
  "customer access": "customers",
  "financial access": "payments",
  "maintenance access": "maintenance",
  "document access": "documents",
};

export type EffectiveAccess = {
  /** Whether the given module is accessible (plan allows it OR always-on) */
  canAccess: (module: string) => boolean;
  /** List of modules the current user can effectively access */
  effectiveModules: string[];
};

/**
 * Simplified access hook — plan-only logic.
 *
 * - No user → canAccess always false, effectiveModules empty.
 * - Super admin → canAccess always true, all modules.
 * - Owner/Staff → canAccess based on plan modules + always-on modules.
 * 
 * Always-on modules (settings, notifications, reports) are never gated by plan.
 */
export function useEffectiveAccess(): EffectiveAccess {
  const { user } = useAuth();

  return useMemo(() => {
    // No user — no access
    if (!user) {
      return {
        canAccess: () => false,
        effectiveModules: [],
      };
    }

    // Super admin — unrestricted
    if (user.role === "super_admin") {
      return {
        canAccess: () => true,
        effectiveModules: [...Object.values(PERMISSION_TO_MODULE), ...ALWAYS_ON_MODULES],
      };
    }

    // ─── Determine plan modules ───────────────────────────────────────────
    const allowedModules = Array.isArray(user.allowedModules) ? user.allowedModules : [];

    // Plan fetch failed — deny everything until session refreshes
    if (allowedModules.includes("__fetch_failed__")) {
      return {
        canAccess: () => false,
        effectiveModules: [],
      };
    }

    // No plan assigned OR empty modules — treat as all modules allowed
    const planModules: Set<string> =
      allowedModules.length === 0
        ? new Set(Object.values(PERMISSION_TO_MODULE))
        : new Set(allowedModules);

    // Always-on modules are always accessible regardless of plan
    const effectiveModules = [...planModules, ...ALWAYS_ON_MODULES];

    return {
      canAccess: (module: string) => ALWAYS_ON_MODULES.has(module) || planModules.has(module),
      effectiveModules,
    };
  }, [user]);
}
